import { api } from './api'

export interface Portfolio {
  id: string
  name: string
  type?: string
  tree?: any
  drafts?: Bounce[]
}

export interface Bounce {
  id: string
  name: string
  isFormal: boolean
  date: string
  user: string
  parent_id?: string
  displayName?: string
}

export interface PortfolioListResponse {
  portfolios: Portfolio[]
  mirrored_portfolio: any[]
  draft_list: { [portfolioId: string]: Bounce[] }
}

class PortfolioService {
  private completePortfolioCache = new Map<string, Promise<any>>()

  invalidatePortfolioCache(bounceId?: string) {
    if (bounceId === undefined) this.completePortfolioCache.clear()
    else this.completePortfolioCache.delete(bounceId)
  }

  async fetchPortfolios(): Promise<PortfolioListResponse> {
    try {
      const response = await api.get('portfolio/list')
      const portfolios = Array.isArray(response.data) ? response.data : []

      const normalizedPortfolios: Portfolio[] = portfolios.map((p: any) => ({
        ...p,
        id: String(p.id)
      }))

      const draft_list: { [portfolioId: string]: Bounce[] } = {}
      for (const p of normalizedPortfolios) {
        if (Array.isArray(p.drafts) && p.drafts.length) {
          draft_list[String(p.id)] = p.drafts.map((d: any) => ({
            ...d,
            id: String(d.id),
            parent_id: d.parent_id ? String(d.parent_id) : undefined
          }))
        }
      }

      return {
        portfolios: normalizedPortfolios,
        mirrored_portfolio: [],
        draft_list
      }
    }
    catch (error) {
      throw new Error('Failed to fetch portfolios')
    }
  }

  extractBouncesFromTree(tree: any): Bounce[] {
    const bounces: Bounce[] = []
    
    if (!tree) return bounces
    
    const processNode = (node: any) => {
      if (node.name && node.nodeID) {
        const parsed = this.parseBounceName(node.name)
        bounces.push({
          id: node.nodeID,
          name: node.name,
          isFormal: parsed.isFormal,
          date: parsed.date,
          user: parsed.user,
          displayName: parsed.displayName,
          parent_id: node.parent_id
        })
      }
      
      if (node.val && node.val.name && node.val.nodeID) {
        const parsed = this.parseBounceName(node.val.name)
        bounces.push({
          id: node.val.nodeID,
          name: node.val.name,
          isFormal: parsed.isFormal,
          date: parsed.date,
          user: parsed.user,
          displayName: parsed.displayName,
          parent_id: node.parent_id
        })
      }
      
      if (node.children && Array.isArray(node.children)) {
        node.children.forEach((child: any) => processNode(child))
      }
    }
    
    processNode(tree)
    return bounces
  }

  /**
   * Parse bounce name to extract information
   */
  parseBounceName(bounceName: string): {
    isFormal: boolean
    date: string
    user: string
    displayName: string
  } {
    try {
      const parts = bounceName.split('-')
      
      if (parts[0] === 'DRAFT') {
        // Draft format: DRAFT-YYYYMMDD-USER-DisplayName
        return {
          isFormal: false,
          date: parts[1] || '',
          user: parts[2] || '',
          displayName: parts[3] || bounceName
        }
      } 
      // Check if any part contains 'F' (formal indicator)
      const isFormal = parts.some(part => part.includes('F'))
        
      if (isFormal) {
        // Formal format: YYYYMM-YYYYMMDDHHMMSSF-USER-DisplayName
        return {
          isFormal: true,
          date: parts[0] || '',
          user: parts[2] || '',
          displayName: parts[3] || bounceName
        }
      } 
      // Non-formal format: YYYYMM-YYYYMMDDHHMMSS-USER-DisplayName
      return {
        isFormal: false,
        date: parts[0] || '',
        user: parts[2] || '',
        displayName: parts[3] || bounceName
      }
        
      
    }
    catch (error) {
      return {
        isFormal: false,
        date: '',
        user: '',
        displayName: bounceName
      }
    }
  }


  formatDate(dateString: string): string {
    try {
      if (dateString.length === 8) {
        // Format: YYYYMMDD
        const year = dateString.slice(0, 4)
        const month = dateString.slice(4, 6)
        const day = dateString.slice(6, 8)
        return `${day}/${month}/${year}`
      }
      else if (dateString.length === 6) {
        // Format: YYYYMM
        const year = dateString.slice(0, 4)
        const month = dateString.slice(4, 6)
        return `${month}/${year}`
      }
      return dateString
    }
    catch {
      return dateString
    }
  }


  async getBouncesForPortfolio(portfolioId: string, portfolios: Portfolio[]): Promise<Bounce[]> {
    const allBounces: Bounce[] = []
    
    const portfolio = portfolios.find(p => p.id === portfolioId)
    
    if (portfolio && portfolio.tree) {
      // Get formal bounces from portfolio tree data
      const formalBounces = this.extractBouncesFromTree(portfolio.tree)
      allBounces.push(...formalBounces)
    }
    
    return allBounces
  }

  async fetchHierarchyValues(
    bounceId: string,
    hierarchyName: string,
    limit = 100,
    page = 1
  ): Promise<string[]> {
    try {
      const response = await api.get('portfolio/values', {
        params: {
          bounce_id: bounceId,
          hierarchy_name: hierarchyName,
          limit,
          page,
        },
      })
      return Array.isArray(response.data?.values) ? response.data.values : []
    }
    catch (error) {
      console.error(`Failed to fetch values for hierarchy '${hierarchyName}':`, error)
      return []
    }
  }

  async fetchCompletePortfolioData(portfolioId: string, bounceId: string): Promise<any> {
    const cached = this.completePortfolioCache.get(bounceId)
    if (cached) {
      const data = await cached
      return JSON.parse(JSON.stringify(data))
    }

    const promise = this.fetchCompletePortfolioDataUncached(portfolioId, bounceId)
    this.completePortfolioCache.set(bounceId, promise)
    try {
      const data = await promise
      return JSON.parse(JSON.stringify(data))
    }
    catch (error) {
      // Don't cache failures — let the next caller retry.
      this.completePortfolioCache.delete(bounceId)
      throw error
    }
  }

  private async fetchCompletePortfolioDataUncached(portfolioId: string, bounceId: string): Promise<any> {
    try {
      const response = await api.get(`portfolio/metadata/${bounceId}`)

      const portfolioData = response.data || {}

      // Create normaliseSelection array (same logic as main app)
      const claimsNature = portfolioData.parameters?.claims_nature || ['ATTRITIONAL', 'LARGE']
      const normaliseSelection: boolean[] = []
      for (let i = 0; i < claimsNature.length - 1; i++) {
        normaliseSelection.push(true)
      }

      const hierarchies: string[] = Array.isArray(portfolioData.parameters?.hierarchies)
        ? portfolioData.parameters.hierarchies
        : []
      const filterOptions: Record<string, string[]> = {}
      if (hierarchies.length > 0) {
        const results = await Promise.all(
          hierarchies.map((h) => this.fetchHierarchyValues(bounceId, h))
        )
        hierarchies.forEach((h, i) => {
          filterOptions[h] = results[i] || []
        })
      }

      const perilTypes: string[] = Array.isArray(portfolioData.peril_types)
        ? portfolioData.peril_types
        : []
      const perilValues: string[][] = Array.isArray(portfolioData.peril_values)
        ? portfolioData.peril_values
        : []
      const claimsHierarchyKeys: string[] = []
      perilTypes.forEach((pt, i) => {
        filterOptions[pt] = Array.isArray(perilValues[i]) ? perilValues[i] : []
        claimsHierarchyKeys.push(pt)
      })

      // Transform to match the expected PortfolioData structure (same as main app's storeCurrentPortfolio)
      return {
        portfolioId: portfolioId,
        portfolioName: '', // Will be set from portfolio list
        bounceId: bounceId,
        bounceName: '', // Will be parsed from bounce name
        bounceDate: '', // Will be parsed from bounce name
        bounceFullName: '', // Will be set from bounce name
        timestamp: Date.now(),
        currentMonth: portfolioData.report_month || '',
        selectedLineSize: portfolioData.parameters?.line_size?.[0] || '',
        uwAcc: 'uw',
        includeProjections: false, // Default value
        yearsOfProjections: 0, // Default value
        isAve: false, // Default value
        claimsNature: claimsNature,
        exposure: portfolioData.parameters?.exposure || [
          { name: 'Total Risk Count', method: 'sum' },
          { name: 'Average GWP', method: 'average' }
        ],
        dashboardExposure: Array.isArray(portfolioData.dashboard_exposure)
          ? portfolioData.dashboard_exposure
          : [],
        exposureColumns: Array.isArray(portfolioData.exposure_columns)
          ? portfolioData.exposure_columns
          : [],
        normaliseSelection: normaliseSelection,
        filterOptions,
        claimsHierarchyKeys,
        parameters: {
          claims_nature: claimsNature,
          exposure: portfolioData.parameters?.exposure || [
            { name: 'Total Risk Count', method: 'sum' },
            { name: 'Average GWP', method: 'average' }
          ],
          // Include other parameters that might be needed
          ...portfolioData.parameters
        }
      }
    }
    catch (error) {
      console.error('Error fetching complete portfolio data:', error)
      throw new Error(`Failed to fetch portfolio data: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }
}

export const portfolioService = new PortfolioService()
